import React from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import { Info } from 'lucide-react';
import { GRAY_400 } from 'theme/grayScale';

export interface InfoTooltipProps {
  text: React.ReactNode;
  size?: number;
  /** Overrides the default "More information" label for a more specific one. */
  ariaLabel?: string;
  /**
   * Overrides the tooltip popper's z-index. Defaults to 10001 — one above
   * the modal overlay's own z-index of 10000 (see AsyncSearchSelect.tsx's
   * `menuPortal` comment and ManufacturerPrecedenceOrderTooltip.tsx's own
   * hardcoded 10001) — MUI's own default (1500) renders behind any modal,
   * which is exactly why every Details-tab InfoTooltip was invisible before
   * this default existed (issue #653, Task 16 QA follow-up). Raise further
   * (e.g. 10003, matching ExternalLinkSuggestionsPane.tsx) only for a
   * tooltip stacked under a SECOND modal on top of the first.
   */
  zIndex?: number;
  /**
   * Opt-in: portals the tooltip's content into a marker node immediately
   * after this button (in DOM/tab order) instead of MUI's default
   * `document.body`, so a plain Tab press reaches any interactive content
   * inside `text` (e.g. a link) -- see `TeCodeHeaderTooltip.tsx` for a real
   * case. Defaults to `false` (the original `document.body` portal) because
   * several existing callers (e.g. `ExternalLinkSuggestionsPane.tsx`'s
   * `resultsArea`, `overflowY: 'auto'`) render this button inside a
   * scrolling container -- portaling into a DOM node that lives inside that
   * container risks the tooltip being clipped by the container's overflow,
   * something the `document.body` portal always escaped. Only set this
   * when `text` contains something a keyboard user actually needs to reach.
   */
  interactive?: boolean;
}

const DEFAULT_TOOLTIP_Z_INDEX = 10001;

/**
 * Accessible info-icon tooltip trigger (issue #653, Task 13, Sub-task
 * 13.2 — Decision 12: "every status-like value shown to a user carries a
 * tooltip... [must be] keyboard accessible"). Renders a real `<button>` so
 * it's naturally focusable/tabbable — MUI's `Tooltip` shows on focus as
 * well as hover for an interactive child, but the bare `<span
 * style={{cursor:'help'}}>` this replaced (StatusSection.tsx's local
 * `InfoTooltip`) has no `tabIndex` and is invisible to keyboard-only
 * navigation entirely. Mirrors the button-based pattern already proven in
 * `PrecedenceOrderTooltip.tsx`/`ManufacturerPrecedenceOrderTooltip.tsx`
 * (both already accessible; not touched here since they already work).
 */
export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  size = 13,
  ariaLabel = 'More information',
  zIndex = DEFAULT_TOOLTIP_Z_INDEX,
  interactive = false,
}) => {
  // See the `interactive` prop doc above -- only mounted/used when opted in,
  // so the 7+ existing non-interactive callers get zero DOM/behavior change.
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null);

  return (
    <>
      <MuiTooltip
        title={text}
        placement="right"
        arrow
        slotProps={{
          popper: {
            style: { zIndex },
            container: interactive ? portalContainer : undefined,
          },
        }}>
        <button
          type="button"
          aria-label={ariaLabel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            color: GRAY_400,
            cursor: 'help',
            background: 'none',
            border: 'none',
            padding: 0,
            margin: '0 0 0 4px',
          }}>
          <Info size={size} />
        </button>
      </MuiTooltip>
      {interactive && (
        <span ref={setPortalContainer} style={{ display: 'contents' }} />
      )}
    </>
  );
};

export default InfoTooltip;
